import { useAppDispatch, useAppSelector, UserCustomer } from 'common';
import { RecipeCustomerShare, ShareRecipeCustomersRequest } from 'common/src/api/models/recipe-customer-share.models';
import { getRecipeCustomerShares, shareRecipeCustomers } from 'common/src/store/recipes';
import { useEffect, useState } from 'react';
import { getResolution } from '../../../../utilities/resolution';
import {
  LocationSearchItem,
  mapToLocationSearchItems,
} from '../../../site/components/common/LocationSearch/LocationSearch';
import LocationSearchCheckboxList from '../../../site/components/common/LocationSearch/LocationSearchCheckboxList';
import LocationSearchDialogBase from '../../../site/components/dialogs/LocationSearchDialog/LocationSearchDialogBase';

export type RecipeCustomerShareDialogProps = {
  customerId: string;
  userCustomers: UserCustomer[] | [];
  onClose: () => void;
};

const RecipeCustomerShareDialog: React.FC<RecipeCustomerShareDialogProps> = (
  props: RecipeCustomerShareDialogProps
): JSX.Element => {
  const { customerId, userCustomers } = props;
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector((s) => s.recipes.isRecipeCustomerSharesLoading);

  const [selectedLocations, setSelectedLocations] = useState<LocationSearchItem[]>([]);

  const handleMap = (recipeShares: RecipeCustomerShare[]) => {
    const matchedCustomerIds = recipeShares
      .map((share) => share.SharedWithCustomerId)
      .filter((sharedId) => userCustomers.some((uc) => uc.CustomerId === sharedId));

    const initialSelectedLocations = mapToLocationSearchItems({
      hideCreditTerm: true,
      ucs: userCustomers,
      selectedCustomerIds: matchedCustomerIds,
    });

    setSelectedLocations(initialSelectedLocations);
  };

  useEffect(() => {
    dispatch(getRecipeCustomerShares(customerId, handleMap));
  }, []);

  const locations = mapToLocationSearchItems({
    hideCreditTerm: true,
    ucs: userCustomers,
    selectedCustomerIds: selectedLocations.filter((loc) => loc.selected).map((loc) => loc.customerId),
  });
  const myLocation = locations.find((l) => l.customerId === customerId);
  if (myLocation) {
    myLocation.disabled = true;
    myLocation.selected = true;
  }
  const saveButtonDisabled: boolean = isLoading;

  const mapSharedRecipes = (locations: LocationSearchItem[]) => {
    return locations.map((location) => ({
      SharedWithCustomerId: location.customerId,
      IsShared: location.selected,
    }));
  };

  const onLocationChange = (locations: LocationSearchItem[]) => {
    setSelectedLocations(locations);
  };

  const onCancel = () => {
    props.onClose && props.onClose();
  };

  const onSubmit = async () => {
    const sharedRecipes = mapSharedRecipes(selectedLocations);
    const req: ShareRecipeCustomersRequest = {
      CustomerId: customerId,
      SharedWithCustomer: sharedRecipes,
    };

    dispatch(shareRecipeCustomers(req, getResolution()));
    onCancel();
  };

  return (
    <LocationSearchDialogBase
      isOpen
      title='Share recipes'
      subTitle='Manage location(s) you share your recipes with:'
      secondaryButtonProps={{ children: 'Cancel', onClick: onCancel }}
      onSubmit={onSubmit}
      onClose={onCancel}
      isDisabled={saveButtonDisabled}
      disableCloseOnSubmit={true}
      contentContainerSx={{ maxWidth: (theme) => ({ lg: theme.spacing(38.5) }) }}
      dialogContainerSx={{ maxWidth: (theme) => ({ lg: theme.spacing(40.5) }) }}
      dialogFooterSx={{ maxWidth: (theme) => ({ lg: theme.spacing(38.5) }) }}
      paperSx={{ width: (theme) => ({ lg: theme.spacing(40.5) + ' !important' }) }}
    >
      <LocationSearchCheckboxList
        items={locations}
        onChange={onLocationChange}
        sx={{ pt: 1 }}
        checkboxListSx={{ width: '100%' }}
      />
    </LocationSearchDialogBase>
  );
};
export default RecipeCustomerShareDialog;
